import { Reveal } from "@components/ui";

const responsibilities = [
  {
    title: "Access for Inspections",
    description: "Allow GAZ access for scheduled and emergency inspections.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M12 8v4l2.5 2.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: "Clean Mechanical Rooms",
    description:
      "Ensure mechanical rooms are clean, ventilated, and inspection-ready.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M3 21h18M5 21V7l7-4 7 4v14"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9 21v-6h6v6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Emergency Exit Access",
    description:
      "Maintain unobstructed emergency exit access to all gas areas.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Emergency Signage",
    description:
      "Display gas leak emergency signage in all required locations.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Restricted Access",
    description:
      "Keep mechanical rooms locked and restricted to authorized personnel.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <rect
          x="3"
          y="11"
          width="18"
          height="11"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M7 11V7a5 5 0 0110 0v4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: "Report Modifications",
    description:
      "Report any structural modifications that may affect gas pipelines.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path
          d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

export function DeveloperResponsibilitiesSection() {
  return (
    <section className="w-full bg-white overflow-hidden px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[100px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 md:gap-14 lg:gap-16">
        {/* Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 max-w-[1112px] mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Property Management
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[54px] font-semibold tracking-[-0.04em] text-[#122d63] leading-[1.1]">
              Developer & Owner Responsibilities
            </h2>
            <p className="text-[14px] md:text-base lg:text-[22px] tracking-[-0.04em] text-[#62636c] max-w-[800px]">
              Key responsibilities for property developers and owner
              associations to maintain gas safety standards.
            </p>
          </div>
        </Reveal>

        {/* Cards Grid */}
        <Reveal direction="up" className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {responsibilities.map((item, index) => (
              <div
                key={index}
                className="group relative rounded-[20px] bg-[#f8f9fb] p-6 md:p-7 flex flex-col gap-5 hover:bg-white hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed] overflow-hidden"
              >
                {/* Subtle number watermark */}
                <span className="absolute top-3 right-5 text-[64px] md:text-[72px] font-bold text-[#122d63]/[0.04] leading-none select-none pointer-events-none">
                  {String(index + 1).padStart(2, "0")}
                </span>

                {/* Icon */}
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-[14px] bg-[#f24d2b]/10 text-[#f24d2b] flex items-center justify-center group-hover:bg-[#f24d2b] group-hover:text-white transition-all duration-300">
                  {item.icon}
                </div>

                {/* Text */}
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-[18px] md:text-[20px] lg:text-[22px] font-semibold tracking-[-0.03em] text-[#122d63]">
                    {item.title}
                  </h3>
                  <p className="text-[13px] md:text-[15px] lg:text-[16px] tracking-[-0.02em] text-[#62636c] leading-[1.5]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
